import { useCallback } from 'react';
import { useTargetingContext } from '../context/TargetingContext';
import { defaultTargetingBlockingRules, useTargetingDisabledState } from './useTargetingDisabledState';

type TargetingChildDisabledState = {
  isDisabled: boolean;
  hasError: boolean;
  reasonMessage?: string;
  errorReasonMessage?: string;
  disabledReasonMessage?: string;
};

export const useTargetingChildDisabledState = (field?: string, fallbackDisabledReasonMessage?: string) => {
  const {
    isDisabled: isParentDisabled,
    disabledReasonMessage: parentDisabledReasonMessage,
    hasError: hasParentError,
    errorReasonMessage: parentErrorReasonMessage,
  } = useTargetingDisabledState(field);

  const targetingContext = useTargetingContext();
  const targetingBlockingRules = targetingContext?.targetingBlockingRules ?? defaultTargetingBlockingRules;

  const getFieldPathForOption = useCallback(
    (option: string) => {
      return field ? `${field}.${option}` : undefined;
    },
    [field]
  );

  const getDisabledStateForOption = useCallback(
    (option: string): TargetingChildDisabledState => {
      const optionFieldPath = getFieldPathForOption(option);

      if (!optionFieldPath) {
        const errorReasonMessage = parentErrorReasonMessage;
        const disabledReasonMessage = parentDisabledReasonMessage ?? fallbackDisabledReasonMessage;

        const reasonMessage = errorReasonMessage ?? disabledReasonMessage;

        return {
          isDisabled: isParentDisabled || hasParentError,
          hasError: hasParentError,
          reasonMessage,
          errorReasonMessage,
          disabledReasonMessage,
        };
      }

      const hasError = Boolean(targetingBlockingRules.hasFieldError?.(optionFieldPath));
      const isDisabled = targetingBlockingRules.isFieldDisabled(optionFieldPath);
      const errorReasonMessage = targetingBlockingRules.getErrorReason?.(optionFieldPath);
      const disabledReasonMessage = targetingBlockingRules.getDisabledReason(optionFieldPath);
      const reasonMessage = errorReasonMessage ?? disabledReasonMessage;

      return {
        isDisabled: hasError || isDisabled || Boolean(reasonMessage),
        hasError,
        reasonMessage,
        errorReasonMessage,
        disabledReasonMessage,
      };
    },
    [
      fallbackDisabledReasonMessage,
      getFieldPathForOption,
      hasParentError,
      isParentDisabled,
      parentDisabledReasonMessage,
      parentErrorReasonMessage,
      targetingBlockingRules,
    ]
  );

  return {
    getFieldPathForOption,
    getDisabledStateForOption,
  };
};
